const DB = 'all-voiced-account-lifecycle';
const STORE = 'recovery';
const SLOT = 'account-closure';
const UUID = '[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}';

export function validateRecoveryBundle(value) {
  if (!value || value.version !== 1 ||
      Object.keys(value).sort().join(',') !== 'credential,dispatched,operationId,operationKey,version' ||
      !new RegExp(`^adl_${UUID}$`).test(value.operationId) ||
      typeof value.operationKey !== 'string' || !value.operationKey.length || value.operationKey.length > 128 ||
      value.operationKey !== value.operationKey.trim() || value.operationKey.includes(',') ||
      !/^als1_[A-Za-z0-9_-]{42}[AEIMQUYcgkosw048]$/.test(value.credential) ||
      typeof value.dispatched !== 'boolean') throw new Error('recovery_unavailable');
  return value;
}

export function generateRecoveryBundle(crypto = typeof window === 'undefined' ? undefined : window.crypto) {
  if (!crypto?.getRandomValues) throw new Error('recovery_unavailable');
  const uuid = () => {
    const bytes = crypto.getRandomValues(new Uint8Array(16));
    bytes[6] = (bytes[6] & 15) | 64; bytes[8] = (bytes[8] & 63) | 128;
    const hex = Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  };
  const bytes = crypto.getRandomValues(new Uint8Array(32));
  const credential = `als1_${btoa(String.fromCharCode(...bytes)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')}`;
  // UUIDv4 satisfies the accepted scalar Idempotency-Key contract (1–128,
  // untrimmed and comma-free), without the legacy non-cryptographic fallback.
  return validateRecoveryBundle({ version: 1, operationId: `adl_${uuid()}`,
    operationKey: uuid(), credential, dispatched: false });
}

export function createLifecycleRecoveryStore({ indexedDB = typeof window === 'undefined' ? undefined : window.indexedDB,
  crypto = typeof window === 'undefined' ? undefined : window.crypto } = {}) {
  const open = () => new Promise((resolve, reject) => {
    if (!indexedDB) return reject(new Error('recovery_unavailable'));
    const request = indexedDB.open(DB, 1);
    let abandoned = false;
    request.onupgradeneeded = () => request.result.createObjectStore(STORE);
    request.onerror = () => reject(new Error('recovery_unavailable'));
    request.onblocked = () => { abandoned = true; reject(new Error('recovery_unavailable')); };
    request.onsuccess = () => {
      if (abandoned) return request.result.close();
      request.result.onversionchange = () => request.result.close();
      resolve(request.result);
    };
  });
  const transaction = async (mode, change) => {
    const db = await open();
    try {
      return await new Promise((resolve, reject) => {
        // No dispatch until the transaction completes, not merely put success.
        const tx = db.transaction(STORE, mode, mode === 'readwrite' ? { durability: 'strict' } : undefined);
        const store = tx.objectStore(STORE);
        let result;
        tx.oncomplete = () => resolve(result);
        tx.onabort = tx.onerror = () => reject(new Error('recovery_unavailable'));
        const request = store.get(SLOT);
        request.onsuccess = () => {
          try { result = change(request.result, store); }
          catch (_) { tx.abort(); }
        };
      });
    } finally { db.close(); }
  };
  return {
    read: () => transaction('readonly', value => value == null ? null : validateRecoveryBundle(value)),
    prepare: () => transaction('readwrite', (value, store) => {
      if (value != null) return validateRecoveryBundle(value);
      const bundle = generateRecoveryBundle(crypto);
      store.add(bundle, SLOT); return bundle;
    }),
    claimDispatch: operationId => transaction('readwrite', (value, store) => {
      const bundle = validateRecoveryBundle(value);
      if (bundle.operationId !== operationId || bundle.dispatched) return false;
      store.put({ ...bundle, dispatched: true }, SLOT); return true;
    }),
    remove: operationId => transaction('readwrite', (value, store) => {
      // Omit operationId only for explicit user removal, including corrupt data.
      if (operationId == null || value?.operationId === operationId) store.delete(SLOT);
    }),
  };
}
